import { ui } from '@procuros/datachecks';

import { useDocumentErrors } from 'services/webediErrors/hooks';
import { DocumentType, DocumentTypeWithVirtual, PartnerDTO, ValueOf } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';
import { SchemableFormVariablesContextProvider } from 'components/SchemableForm/contexts/SchemableFormVariablesContext/SchemableFormVariablesContextProvider';

import { DocumentBaseSection } from '../DocumentBaseSection/DocumentBaseSection';
import { DocumentHeaderInfo } from '../DocumentHeaderInfo/DocumentHeaderInfo';
import { DocumentLineItems } from '../DocumentLineItems/DocumentLineItems';
import { DocumentLocations } from '../DocumentLocations/DocumentLocations';
import { DocumentModificationGroups } from '../DocumentModificationGroups/DocumentModificationGroups';
import { DocumentParties } from '../DocumentParties/DocumentParties';
import { DocumentPaymentTerms } from '../DocumentPaymentTerms/DocumentPaymentTerms';
import { DocumentTransportUnits } from '../DocumentTransportUnits/DocumentTransportUnits';

type BaseProps = {
  uiConfig: Array<ui.Section> | undefined;
  documentType: ValueOf<DocumentType>;
  variables: Record<string, string>;
};

type EditableProps = BaseProps & {
  mode: DisplayModes.create | DisplayModes.edit;
  isSubmitting: boolean;
};

type ViewProps = BaseProps & {
  mode: DisplayModes.view;
  isSubmitting?: boolean;
};

type DocumentSectionsProps = (EditableProps | ViewProps) & {
  senderPartner: Pick<PartnerDTO, 'name' | 'id'> | null | undefined;
  receiverPartner: Pick<PartnerDTO, 'name' | 'id'> | null | undefined;
};

export const DocumentSections = ({
  mode,
  uiConfig,
  documentType,
  variables,
  isSubmitting,
  senderPartner,
  receiverPartner,
}: DocumentSectionsProps) => {
  useDocumentErrors(uiConfig, mode, variables, documentType as DocumentTypeWithVirtual, senderPartner, receiverPartner);
  return (
    <SchemableFormVariablesContextProvider variables={variables}>
      <div className="h-full space-y-8 divide-y divide-gray-200 [&>*:first-child]:pt-0 [&>*]:pt-6">
        {uiConfig?.map((config) => {
          switch (config.type) {
            case 'info':
              return (
                <DocumentHeaderInfo
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                />
              );
            case 'parties':
              return (
                <DocumentParties key={config.type} mode={mode} config={config} isSubmitting={Boolean(isSubmitting)} />
              );
            case 'transport_units':
              return (
                <DocumentTransportUnits
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                />
              );
            case 'locations':
              return (
                <DocumentLocations key={config.type} mode={mode} config={config} isSubmitting={Boolean(isSubmitting)} />
              );
            case 'line_items':
              return (
                <DocumentLineItems
                  key={config.type}
                  mode={mode}
                  config={config}
                  documentType={documentType as DocumentType}
                  isSubmitting={Boolean(isSubmitting)}
                />
              );
            case 'payment_terms':
              return (
                <DocumentPaymentTerms
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                />
              );
            case 'global_modifications':
              return (
                <DocumentModificationGroups
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                />
              );
            default:
              return (
                <DocumentBaseSection
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                />
              );
          }
        })}
      </div>
    </SchemableFormVariablesContextProvider>
  );
};
